import React, { useContext, useEffect } from "react";
import styles from "./styles/NotFound.module.css";
import DataContext from "../../context/DataContext";
import NavButton from "../../components/ui/navbutton/NavButton";
import useWindowSize from "../../hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useCurrentBU from "../../hooks/useCurrentBU";
import useMaxHeightOfChildren from "../../hooks/useMaxHeightOfChildren";

/**
 * NotFound component
 * This component renders a custom 404 error page.
 * The 404 image is embedded as an SVG within the component, allowing for dynamic color changes.
 */
const NotFound = ({ footerRef }) => {
    const {
        currentCustom, // {Object} - Customization data for the current business user
        serviceData, // {Function} - Function to fetch service data
        isServiceDataAvailable, // {Boolean} - Flag to check if service data is available
        currentBU, // {String} - Current business user
        paymentHeaderFooterHeight, // {Float} - calculated height of the header and footer
        setPaymentHeaderFooterHeight, // {Function} - Function to set the calculated height of the header and footer
        FOOTER_OFFSET, // {integer} - the offset value that needs to be added to the footers cards max height
        JWTToken,
    } = useContext(DataContext);

    const { t } = useTranslation(); // Hook for handling translations using i18next.
    const location = useLocation();

    // Get the height of the footer and header
    const footerHeaderHeight = useMaxHeightOfChildren(
        "footerCardsContainer",
        FOOTER_OFFSET,
        footerRef,
    );

    const windowSize = useWindowSize(); // Custom hook to get the current window size.

    const { bUser } = useParams(); // Extracts 'bUser' parameter from URL.

    useCurrentBU(bUser);

    // Set the height of the header and footer in the data context.
    useEffect(() => {
        setPaymentHeaderFooterHeight(footerHeaderHeight);
    }, [footerHeaderHeight]);

    // Fetch service data if 'bUser' is provided and service data is not available.
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    return (
        isServiceDataAvailable && (
            <>
                <article
                    className={`${styles.image404_page_container}   main-page-container flex-container`}
                    style={{
                        minHeight: `calc(100vh - ${paymentHeaderFooterHeight}px)`,
                    }}
                >
                    <section
                        className={`${styles.image404_main_container} main-container flex-container`}
                    >
                        <div
                            className={`${styles.image404_media_container} v-flex-container`}
                        >
                            <h1 className={`${styles.image404_text} `}>
                                {t(`page not found`)}
                            </h1>
                            <div
                                className={`${styles.image404_button_container}`}
                            >
                                <NavButton
                                    body={t(`home page`)}
                                    link={
                                        location?.pathname.includes("/pos")
                                            ? `/${bUser}/pos`
                                            : `/${bUser}`
                                    }
                                />
                            </div>
                        </div>
                        <div className={`${styles.image404}`}>
                            <svg
                                viewBox="0 0 329 432.8"
                                width={
                                    windowSize.width <= 480 ? "15rem" : "20rem"
                                }
                            >
                                <use href="#image404"></use>
                            </svg>
                        </div>
                    </section>
                    {/* 404 image svg */}
                    <svg
                        className={`display-non`}
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                    >
                        <symbol
                            id="image404"
                            data-name="image404"
                            viewBox="0 0 329 432.8"
                        >
                            <path
                                d="M226.1,394.1v-209h-12.7v208.8c-10.7,2.5-18.6,13.3-18.6,25.2h49.2c0-11.7-7.6-22.3-17.9-25Z"
                                fill="#031b26"
                                fillRule="evenodd"
                                strokeWidth="0"
                            />
                            <path
                                d="M321.5,98.2l-50.9,88.2h-101.8l-50.9-88.2L168.8,10h101.8l50.9,88.2h0Z"
                                fill={`${currentCustom?.color?.current}`} // Fill color changes dynamicaly
                                strokeWidth="0"
                            />
                            <path
                                d="M289.2,53.6h6.6l-25.2-43.7h-101.8l-50.9,88.2,21.3,37c33-49.3,87.8-81.6,149.9-81.6h.1Z"
                                fill="#fff"
                                fillOpacity=".5"
                                strokeWidth="0"
                            />
                            <path
                                d="M266.2,19.8l-.6-1h-91.7l-45.9,79.4,45.3,78.4.6,1h91.7l45.9-79.4-45.3-78.4h0ZM263.3,173.7h-87.1l-43.5-75.4,43.5-75.4h87.1l43.5,75.4-43.5,75.4h0Z"
                                fill="#fff"
                                strokeWidth="0"
                            />
                            <path
                                d="M273.2,125.4c-.5,0-1-.2-1.4-.5-.4-.4-.5-.8-.5-1.4v-8.7h-23.9c-.5,0-1-.2-1.4-.5-.4-.4-.5-.8-.5-1.4v-7.3c0-.5,0-.9.2-1.2.2-.4.4-.8.6-1.2l22.4-30.3c.7-.9,1.6-1.3,2.8-1.3h11.1c.6,0,1,.2,1.4.5.4.4.6.8.6,1.4v29.8h6.8c.6,0,1.1.2,1.5.6.4.4.5.8.5,1.3v7.6c0,.6-.2,1-.6,1.4s-.8.5-1.3.5h-6.8v8.7c0,.6-.2,1-.6,1.4s-.8.5-1.4.5h-9.5ZM258.7,103.9h12.9v-18.1l-12.9,18.1Z"
                                fill="#fff"
                                strokeWidth="0"
                            />
                            <path
                                d="M219.5,126.2c-3.7,0-6.9-.5-9.6-1.5-2.7-1.1-5-2.5-6.8-4.4-1.7-1.8-3.1-4-4-6.5s-1.5-5.1-1.6-7.9c0-1.4-.1-2.9-.2-4.7v-5.4c0-1.8.1-3.5.2-4.9.2-2.8.7-5.4,1.6-7.8,1-2.5,2.4-4.6,4.1-6.4,1.8-1.8,4.1-3.3,6.8-4.3,2.7-1,5.9-1.5,9.5-1.5s6.8.5,9.4,1.5c2.7,1,4.9,2.5,6.7,4.3,1.8,1.8,3.3,3.9,4.2,6.4,1,2.4,1.5,5,1.6,7.8.1,1.4.2,3.1.2,4.9v5.4c0,1.7,0,3.3-.2,4.7-.1,2.8-.6,5.5-1.6,7.9-.9,2.5-2.3,4.6-4.1,6.5-1.7,1.8-4,3.3-6.7,4.4-2.7,1-5.9,1.5-9.6,1.5h.1ZM219.5,115c2.7,0,4.6-.8,5.8-2.5,1.2-1.7,1.9-4.1,1.9-7,.1-1.5.2-3,.2-4.7v-4.9c0-1.6,0-3.1-.2-4.5,0-2.8-.7-5.1-1.9-6.8-1.2-1.7-3.1-2.6-5.8-2.7-2.7,0-4.6.9-5.8,2.7-1.2,1.7-1.8,4-1.9,6.8,0,1.4-.1,2.9-.2,4.5v4.9c0,1.6.1,3.2.2,4.7.1,2.9.8,5.2,2,7,1.2,1.7,3.1,2.5,5.7,2.5h0Z"
                                fill="#fff"
                                strokeWidth="0"
                            />
                            <path
                                d="M173.3,125.4c-.5,0-1-.2-1.4-.5-.4-.4-.5-.8-.5-1.4v-8.7h-23.9c-.5,0-1-.2-1.4-.5-.4-.4-.5-.8-.5-1.4v-7.3c0-.5,0-.9.2-1.2.2-.4.4-.8.6-1.2l22.4-30.3c.7-.9,1.6-1.3,2.8-1.3h11.1c.6,0,1,.2,1.4.5.4.4.6.8.6,1.4v29.8h6.8c.6,0,1.1.2,1.5.6.4.4.5.8.5,1.3v7.6c0,.6-.2,1-.6,1.4s-.8.5-1.3.5h-6.8v8.7c0,.6-.2,1-.6,1.4s-.8.5-1.4.5h-9.5ZM158.7,103.9h12.9v-18.1l-12.9,18.1Z"
                                fill="#fff"
                                strokeWidth="0"
                            />
                            <polygon
                                points="75.4 276.1 109.7 276.1 151.4 395.4 33.8 395.4 75.4 276.1"
                                fill="#fff"
                                strokeWidth="0"
                            />
                            <g>
                                <path
                                    d="M169,405.8h-14.3c0-.6-.1-1.2-.3-1.8l-3-8.6H33.8l-3,8.6c-.2.6-.3,1.2-.3,1.8h-14.3c-4.7,0-8.6,3.8-8.6,8.6s3.8,8.6,8.6,8.6h152.9c4.7,0,8.6-3.8,8.6-8.6s-3.8-8.6-8.6-8.6h-.1Z"
                                    fill={`${currentCustom?.color?.current}`} // Fill color changes dynamicaly
                                    strokeWidth="0"
                                />
                                <polygon
                                    points="7.5 414.4 7.5 414.4 7.5 414.4 7.5 414.4"
                                    fill={`${currentCustom?.color?.current}`} // Fill color changes dynamicaly
                                    strokeWidth="0"
                                />
                                <polygon
                                    points="177.6 414.4 177.6 414.4 177.6 414.4 177.6 414.4"
                                    fill={`${currentCustom?.color?.current}`} // Fill color changes dynamicaly
                                    strokeWidth="0"
                                />
                                <path
                                    d="M98.2,243.1c-1.9-5.4-9.5-5.4-11.3,0l-11.5,33h34.3l-11.5-33Z"
                                    fill={`${currentCustom?.color?.current}`} // Fill color changes dynamicaly
                                    strokeWidth="0"
                                />
                                <polygon
                                    points="123.8 316.3 61.3 316.3 47.6 355.9 137.6 355.9 123.8 316.3"
                                    fill={`${currentCustom?.color?.current}`} // Fill color changes dynamicaly
                                    strokeWidth="0"
                                />
                            </g>
                            <path
                                d="M103.8,276.1l-10.1-36.9c-.7-.1-1.5-.1-2.2,0l2.5,36.9h9.8ZM114.8,316.3h-18l2.7,39.5h26.1l-10.8-39.5ZM102.9,405.8l-.7-10.4h34.2l2.9,10.4h-36.4Z"
                                fill="#fff"
                                fillOpacity=".5"
                                fillRule="evenodd"
                                strokeWidth="0"
                            />
                        </symbol>
                    </svg>
                </article>
            </>
        )
    );
};

export default NotFound;
